import { TOUR_CONFIG } from '../config/tour.config'

/**
 * Resolve when an element matching `selector` exists in the DOM. Uses a single
 * MutationObserver scoped to <body> that disconnects the moment the element
 * appears (or the timeout fires) — no polling loops left running.
 */
export function waitForElement(
  selector: string,
  options: { timeout?: number; signal?: AbortSignal } = {},
): Promise<Element | null> {
  const timeout = options.timeout ?? TOUR_CONFIG.targetWaitTimeout
  const existing = document.querySelector(selector)
  if (existing) return Promise.resolve(existing)

  return new Promise((resolve) => {
    let settled = false
    const finish = (el: Element | null) => {
      if (settled) return
      settled = true
      observer.disconnect()
      window.clearTimeout(timer)
      options.signal?.removeEventListener('abort', onAbort)
      resolve(el)
    }
    const onAbort = () => finish(null)
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector)
      if (el) finish(el)
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const timer = window.setTimeout(() => finish(document.querySelector(selector)), timeout)
    options.signal?.addEventListener('abort', onAbort)
  })
}

/** Resolve on the next animation frame (used to let a route's DOM settle). */
export function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}
